import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Star,
  MapPin,
  Users,
  Building2,
  TrendingUp,
  Eye,
  DollarSign,
  BarChart3,
  Target,
  Filter,
  Download,
  MoreVertical,
  Loader2,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/lib/supabaseClient";

interface PropertyTemplate {
  id: string;
  name: string;
  country: string;
  city: string;
  photo_url: string;
  type: string;
  stars: number;
  currency: string;
  gfa_sqm: number;
  purchase_price: number;
  total_rooms: number;
  rooms_total: number;
  facilities: string[];
  featured: boolean;
  show_in_templates: boolean;
  show_in_gallery: boolean;
  created_at: string;
  updated_at: string;
}

export default function AdminDeals() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const [templates, setTemplates] = useState<PropertyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<PropertyTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [userId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      let query = supabase.from("properties").select(`
        id,
        name,
        country,
        city,
        photo_url,
        type,
        stars,
        currency,
        gfa_sqm,
        purchase_price,
        total_rooms,
        rooms_total,
        facilities,
        featured,
        show_in_templates,
        show_in_gallery,
        created_at,
        updated_at
      `);

      if (userId) {
        query = query.eq("created_by", userId);
      }

      const { data, error } = await query.order("created_at", {
        ascending: false,
      });

      if (error) throw error;

      setTemplates(data || []);
    } catch (error) {
      console.error("Failed to load templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter((t) => {
    const name = (t.name || "").toLowerCase();
    const city = (t.city || "").toLowerCase();
    const country = (t.country || "").toLowerCase();
    const searchLower = searchQuery.toLowerCase();

    return (
      name.includes(searchLower) ||
      city.includes(searchLower) ||
      country.includes(searchLower)
    );
  });

  const totalTemplates = templates.length;
  const featuredCount = templates.filter((t) => t.featured).length;
  const avgPrice =
    templates.reduce((sum, t) => sum + (t.purchase_price || 0), 0) /
    Math.max(templates.length, 1);
  const totalRooms = templates.reduce(
    (sum, t) => sum + (t.total_rooms || 0),
    0,
  );

  const formatPrice = (
    price: number | undefined,
    currency: string | undefined,
  ) => {
    if (!price) return `${currency || ""} 0`;
    if (price >= 1000000)
      return `${currency || ""} ${(price / 1000000).toFixed(1)}M`;
    if (price >= 1000) return `${currency || ""} ${(price / 1000).toFixed(0)}K`;
    return `${currency || ""} ${price.toLocaleString()}`;
  };

  const handleEdit = (template: PropertyTemplate) => {
    navigate(`/admin/deals/edit/${template.id}`);
  };

  const handleCreate = () => {
    navigate("/admin/deals/create");
  };

  const handleDelete = (template: PropertyTemplate) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedTemplate) return;

    try {
      const { error } = await supabase
        .from("properties")
        .delete()
        .eq("id", selectedTemplate.id);

      if (error) throw error;

      setTemplates(templates.filter((t) => t.id !== selectedTemplate.id));
      toast.success(`"${selectedTemplate.name}" deleted`);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete template");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
    }
  };

  const getPropertyTypeColor = (type: string | undefined) => {
    if (!type) return "bg-gray-100 text-gray-800";
    switch (type.toLowerCase()) {
      case "hotel":
        return "bg-blue-100 text-blue-800";
      case "resort":
        return "bg-emerald-100 text-emerald-800";
      case "hostel":
        return "bg-orange-100 text-orange-800";
      case "boutique hotel":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const getImageUrl = (photoUrl: string | undefined) => {
    if (!photoUrl || photoUrl.trim() === "") {
      return null;
    }
    if (!photoUrl.startsWith("http")) {
      return `https://${photoUrl}`;
    }
    return photoUrl;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-gray-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Property Templates
            </h1>
            <p className="text-muted-foreground mt-1">
              {userId
                ? "Viewing templates created by specific user"
                : "Manage property templates that users can select to create new deals"}
            </p>
          </div>
          <Button onClick={handleCreate} className="gap-2 bg-primary-blue">
            <Plus className="h-4 w-4" />
            New Template
          </Button>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Total Templates
                  </p>
                  <p className="text-2xl font-bold">{totalTemplates}</p>
                </div>
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {featuredCount} featured
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Total Room Keys
                  </p>
                  <p className="text-2xl font-bold">{totalRooms}</p>
                </div>
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Target className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Across all templates
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg. Price</p>
                  <p className="text-2xl font-bold">
                    {formatPrice(avgPrice, templates[0]?.currency || "EUR")}
                  </p>
                </div>
                <div className="p-2 bg-amber-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Last Updated</p>
                  <p className="text-2xl font-bold">
                    {templates[0] ? formatDate(templates[0].updated_at) : "-"}
                  </p>
                </div>
                <div className="p-2 bg-violet-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-violet-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates by name, city or country..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Button variant="outline" className="gap-2" onClick={loadTemplates}>
            <Filter className="h-4 w-4" />
            Refresh
          </Button>

          {userId && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => navigate("/admin/deals")}
            >
              Show All Templates
            </Button>
          )}

          <div className="text-sm text-muted-foreground">
            {templates.filter((t) => t.show_in_templates).length} visible to
            users
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">All Property Templates</CardTitle>
              <div className="text-sm text-muted-foreground">
                {filteredTemplates.length} of {templates.length} templates
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[300px]">Property</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Type & Settings</TableHead>
                  <TableHead className="text-center">Details</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.map((template) => {
                  const imageUrl = getImageUrl(template.photo_url);

                  return (
                    <TableRow key={template.id} className="hover:bg-gray-50/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={template.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src =
                                    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFNUU1RTUiLz48cGF0aCBkPSJNMTIwIDEwMEwxMDAgODAgODAgMTAwbDIwIDIwIDIwLTIwIDIwIDIwIDIwLTIwTDEyMCAxMDBaTTEwMCAxNDBBNDAgNDAgMCAxIDAgMTAwIDYwYTQwIDQwIDAgMCAwIDAgODBaIiBmaWxsPSIjQzVDNUM1Ii8+PC9zdmc+";
                                  e.currentTarget.onerror = null;
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                <Building2 className="h-6 w-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900 truncate">
                                {template.name}
                              </p>
                              {template.featured && (
                                <Badge
                                  variant="outline"
                                  className="border-amber-200 bg-amber-50 text-amber-700"
                                >
                                  <Star className="h-3 w-3 fill-amber-500 text-amber-500 mr-1" />
                                  Featured
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {template.stars > 0 && (
                                <>
                                  <div className="flex items-center gap-1">
                                    {Array.from({
                                      length: template.stars,
                                    }).map((_, i) => (
                                      <Star
                                        key={i}
                                        className="h-3 w-3 fill-amber-400 text-amber-400"
                                      />
                                    ))}
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    •
                                  </span>
                                </>
                              )}
                              <span className="text-xs text-gray-500">
                                {template.total_rooms || 0} rooms
                              </span>
                              <span className="text-xs text-gray-500">•</span>
                              <span className="text-xs text-gray-500">
                                {formatDate(template.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <div>
                            <div className="font-medium text-gray-900">
                              {template.city || "Unknown"}
                            </div>
                            <div className="text-xs text-gray-500">
                              {template.country || ""}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge
                            className={`${getPropertyTypeColor(template.type)} capitalize`}
                          >
                            {template.type || "Unknown"}
                          </Badge>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-2 h-2 rounded-full ${template.show_in_templates ? "bg-green-500" : "bg-gray-300"}`}
                              />
                              <span className="text-xs text-gray-500">
                                {template.show_in_templates
                                  ? "Visible in templates"
                                  : "Hidden from templates"}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-2 h-2 rounded-full ${template.show_in_gallery ? "bg-green-500" : "bg-gray-300"}`}
                              />
                              <span className="text-xs text-gray-500">
                                {template.show_in_gallery
                                  ? "Visible in gallery"
                                  : "Hidden from gallery"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="space-y-1">
                          <div className="flex items-center justify-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">
                              {template.total_rooms || 0}
                            </span>
                            <span className="text-sm text-gray-500">rooms</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {(template.gfa_sqm || 0).toLocaleString()} sqm
                          </div>
                          <div className="text-xs text-gray-500">
                            {template.facilities?.length || 0} facilities
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-bold text-gray-900">
                          {formatPrice(
                            template.purchase_price,
                            template.currency,
                          )}
                        </div>
                        {template.rooms_total > 0 && (
                          <div className="text-xs text-gray-500">
                            {template.currency === "EUR"
                              ? "€"
                              : template.currency === "USD"
                                ? "$"
                                : template.currency || ""}
                            {Math.round(
                              (template.purchase_price || 0) /
                                template.rooms_total,
                            ).toLocaleString()}{" "}
                            per key
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(template)}
                            className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent
                              align="end"
                              className="min-w-[160px]"
                            >
                              <DropdownMenuItem
                                onClick={() => handleEdit(template)}
                                className="cursor-pointer py-2"
                              >
                                <Edit className="h-4 w-4 mr-2 text-gray-500" />
                                <span>Edit Template</span>
                              </DropdownMenuItem>

                              <DropdownMenuSeparator />

                              <DropdownMenuItem
                                onClick={() => handleDelete(template)}
                                className="cursor-pointer py-2 text-red-600 focus:text-red-600 focus:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                <span>Delete</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {filteredTemplates.length === 0 && (
              <div className="text-center py-12">
                <div className="mx-auto max-w-xs">
                  <div className="rounded-full bg-gray-100 p-4 w-fit mx-auto">
                    <Search className="h-8 w-8 text-gray-400" />
                  </div>
                  <h4 className="mt-4 text-lg font-semibold text-gray-900">
                    {templates.length === 0
                      ? "No templates yet"
                      : "No templates found"}
                  </h4>
                  <p className="mt-2 text-gray-600">
                    {templates.length === 0
                      ? "Create your first property template to get started."
                      : "No templates match your search criteria. Try a different search term."}
                  </p>
                  {templates.length === 0 && (
                    <Button onClick={handleCreate} className="mt-4 gap-2">
                      <Plus className="h-4 w-4" />
                      Create First Template
                    </Button>
                  )}
                  {templates.length > 0 && (
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => setSearchQuery("")}
                    >
                      Clear Search
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Recently Added
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {templates.slice(0, 3).map((template) => (
                  <div
                    key={template.id}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded overflow-hidden bg-gray-100">
                        {getImageUrl(template.photo_url) ? (
                          <img
                            src={getImageUrl(template.photo_url)!}
                            alt={template.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <span className="text-sm font-medium truncate max-w-[120px]">
                        {template.name}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {template.city || "Unknown"}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                By Property Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Array.from(new Set(templates.map((t) => t.type))).map(
                  (type) => {
                    const count = templates.filter(
                      (t) => t.type === type,
                    ).length;
                    if (!type || count === 0) return null;

                    return (
                      <div
                        key={type}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-3 h-3 rounded-full ${getPropertyTypeColor(type).split(" ")[0]}`}
                          />
                          <span className="text-sm font-medium capitalize">
                            {type}
                          </span>
                        </div>
                        <Badge
                          variant="outline"
                          className={`text-xs ${getPropertyTypeColor(type)}`}
                        >
                          {count}
                        </Badge>
                      </div>
                    );
                  },
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleCreate}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Template
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={loadTemplates}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Refresh List
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate("/admin/properties/underwriting")}
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Underwriting Defaults
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Delete Property Template
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTemplate?.name}"? This
              action cannot be undone.
              {selectedTemplate?.featured && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
                  ⚠️ This is a featured template. Removing it may affect user
                  experience.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
